import { useTranslation } from 'react-i18next';
import styles from './ContactDetail.module.scss';
import classNames from 'classnames/bind';
import { PlusIcon, TrashIcon } from '~/assets/icons';
import { Box, Button, Tab, Tabs } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { STATUS_TABS_CONTACT_DETAILS } from '~/utils/constants/contact';
import { handleGetSelectAccounts } from '~/thunks/account/accountThunk';
import { taskActions } from '~/thunks/task/taskSlice';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import TaskList from './components/task/TaskList';
import EditTaskDialog from '../components/EditTaskDialog';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { handleGetTask } from '~/thunks/task/taskThunk';
import { ICustomer } from '~/utils/interface/customer';
import { IPayloadDeleteTasks, ITask } from '~/utils/interface/task';
import { handleGetContactDetail } from '~/thunks/customer/customerThunk';
import taskApi from '~/apis/task';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import BasicInfo from './components/BasicInfo/BasicInfo';
import { BaseHeaderTitle, BaseImage } from '~/components/common';
import File from './components/File/File';
import ContactActivity from './components/Activities/ContactActivities';
import ActivitiesNotes from '../../customers/CustomerDetail/Notes/Notes';
import { handleGetJobs, handleGetTemplate } from '~/thunks/job/jobThunk';
import SendEmail from './components/SendEmail/SendEmail';
import JobList from '../../jobs/components/JobList';
import EditCustomerDialog from '~/components/customer/edit-customer-dialog/EditCustomerDialog';

const cx = classNames.bind(styles);

const ContactDetail = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { id } = useParams();
  const permissions = usePermission();
  const { t } = useTranslation('admin/contact');
  const dispatch = useAppDispatch();
  const { filter } = useAppSelector((state) => state.job);
  const { hash } = useLocation();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [refreshCustomer, setRefreshCustomer] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>('');
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openEditTaskDialog, setOpenEditTaskDialog] = useState<boolean>(false);
  const [editTaskDialogMode, setEditTaskDialogMode] = useState<string>('');
  const [task, setTask] = useState<ITask>();
  const [customer, setCustomer] = useState<ICustomer>();
  const [page, setPage] = useState<string>('basicInfo');
  const { selectedIds, menuSelectedId, openAlertModalDelete } = useAppSelector((state) => state.task);
  const [openSendMailDialog, setOpenSendMailDialog] = useState<boolean>(false);
  const tabHeaders = useMemo(() => {
    const tabContent: Array<string> = [];
    STATUS_TABS_CONTACT_DETAILS.forEach((item) => {
      if (
        (item === 'basicInfo' && permissions[PERMISSIONS.contact_view]) ||
        (item === 'tasks' && permissions[PERMISSIONS.contactTask_view]) ||
        (item === 'notes' && permissions[PERMISSIONS.contactNote_view]) ||
        (item === 'activities' && permissions[PERMISSIONS.contactActivity_view]) ||
        (item === 'files' && permissions[PERMISSIONS.contactFile_view]) ||
        (item === 'jobs' && permissions[PERMISSIONS.project_view])
      ) {
        tabContent.push(item);
      }
    });

    return tabContent;
  }, [permissions]);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    dispatch(handleGetSelectAccounts({}));
    dispatch(taskActions.changeMode(DIALOG_MODE_CREATE));
  }, [dispatch]);

  useEffect(() => {
    if (id || (id && refreshCustomer)) {
      setRefreshCustomer(false);
      (async () => {
        try {
          const customer = await dispatch(handleGetContactDetail(id.toString())).unwrap();
          setCustomer(customer);
        } catch (e) {
          handleApiError(e);
        }
      })();
    }
  }, [id, dispatch, refreshCustomer]);
  useEffect(() => {
    dispatch(handleGetTemplate({ params: { type: 'email' } }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch]);

  // Fetching jobs and customers for the customer details
  useEffect(() => {
    dispatch(handleGetJobs({ ...filter, jobCustomerId: id }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, id]);

  useEffect(() => {
    if (hash) setPage(hash.slice(1));
  }, [hash]);
  //#endregion Implement Hook

  //#region Handle Function

  const handleOpenEditTask = async (id: string) => {
    try {
      const response = await dispatch(handleGetTask(id)).unwrap();
      setTask(response);
      setEditTaskDialogMode(DIALOG_MODE_EDIT);
      setOpenEditTaskDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    navigate(`#${newValue}`);
  };

  const handleClickTask = () => {
    setEditTaskDialogMode(DIALOG_MODE_CREATE);
    setOpenEditTaskDialog(true);
  };

  const handleSetOpen = async (open: boolean, needRefreshData?: boolean) => {
    setEditTaskDialogMode('');
    setTask(undefined);
    setOpenEditTaskDialog(open);
    if (needRefreshData) dispatch(taskActions.changeNeedRefreshData(true));
  };
  const handleCloseDialog = async (open: boolean) => {
    setOpenSendMailDialog(open);
  };

  const handleClickSendMail = async () => {
    setOpenSendMailDialog(true);
  };
  const openAlertDeleteModal = useCallback(() => {
    dispatch(taskActions.changeOpenModalDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(taskActions.changeMenuSelectId(undefined));
    dispatch(taskActions.changeOpenModalDelete(false));
  }, [dispatch]);
  const handleEditContactInfo = async () => {
    const response = await dispatch(handleGetContactDetail(id ?? '')).unwrap();
    setCustomer(response);
    setEditDialogMode(DIALOG_MODE_EDIT);
    setOpenEditDialog(true);
  };
  const handleSetOpenEdit = async (open: boolean, needRefreshData?: boolean) => {
    setOpenEditDialog(open);
    needRefreshData && setRefreshCustomer(needRefreshData);
  };
  const onDeleteSelectedTasks = useCallback(async () => {
    let payloadDeleteAccounts: IPayloadDeleteTasks =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await taskApi.deleteTasks(payloadDeleteAccounts);
      notify('success', t('deleteSuccessMessage', { ns: 'admin/common' }));
      dispatch(taskActions.changeNeedRefreshData(true));
      dispatch(taskActions.changeOpenModalDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);
  const renderTabContent = () => {
    switch (page) {
      case 'basicInfo':
        return <BasicInfo data={customer} openEditDialog={handleEditContactInfo} />;
      case 'notes':
        return id && permissions[PERMISSIONS.contactNote_view] ? <ActivitiesNotes id={id} isContact={true} /> : null;
      case 'tasks':
        return id && permissions[PERMISSIONS.contactTask_view] ? (
          <TaskList id={id} onOpenEdit={handleOpenEditTask} />
        ) : null;
      case 'files':
        return <File data={customer} id={id} />;
      case 'activities':
        return id && permissions[PERMISSIONS.contactActivity_view] ? <ContactActivity id={id} /> : null;
      case 'jobs':
        return (
          permissions[PERMISSIONS.project_view] && (
            <JobList onOpenEdit={() => {}} hideMenuButton={true} hideColumns={['customer']} />
          )
        );
      default:
        return null;
    }
  };
  //#endregion Handle Function

  return (
    <>
      <Box className='w-full h-full'>
        <Box className='flex'>
          <BaseImage src={customer?.photo} className='w-[38px] h-[38px] mr-[10px]' />
          <BaseHeaderTitle className='mb-[20px]' title={customer?.name || t('contact.title')} />
        </Box>
        <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center mb-[50px]'>
          <Box className={cx('tab-list-container')}>
            <Tabs value={page} onChange={handleChange}>
              {tabHeaders.map((status: string) => (
                <Tab
                  value={status}
                  key={status}
                  label={t(`contactHeader.${status}`)}
                  className={cx('tab-item-container')}
                />
              ))}
            </Tabs>
          </Box>
          {permissions[PERMISSIONS.send_email] && (
            <Button
              className='w-[123px] h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] mt-[10px] ml-[10px]'
              variant='contained'
              disableElevation
              onClick={handleClickSendMail}
            >
              {t('sendMail')}
            </Button>
          )}
        </Box>
        <SendEmail open={openSendMailDialog} setOpen={handleCloseDialog} id={id} />
        {page === 'tasks' ? (
          <Box className='flex justify-end'>
            {permissions[PERMISSIONS.contact_delete] && (
              <Button
                className={`${cx(
                  { 'visibility: visible': selectedIds.length > 0 },
                  { 'visibility: hidden': selectedIds.length <= 0 }
                )} w-[107px] h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] mt-[10px] mr-[10px]`}
                onClick={openAlertDeleteModal}
                variant='contained'
                color='error'
                disableElevation
                startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
              >
                {t('delete')}
              </Button>
            )}
            {permissions[PERMISSIONS.contactTask_create] && (
              <Button
                className='w-[107px] h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] mt-[10px]'
                variant='contained'
                disableElevation
                startIcon={<PlusIcon className='[&>path]:stroke-[#ffffff] w-[10px] h-[10px]' />}
                onClick={handleClickTask}
              >
                {t('addNew')}
              </Button>
            )}
          </Box>
        ) : (
          <></>
        )}
        {renderTabContent()}
        <EditCustomerDialog open={openEditDialog} mode={editDialogMode} setOpen={handleSetOpenEdit} data={customer} />
        <EditTaskDialog
          open={openEditTaskDialog}
          mode={editTaskDialogMode}
          setOpen={handleSetOpen}
          data={task}
          customer={customer}
        />
        <ModalAlertDelete
          open={openAlertModalDelete}
          description={t('alertDescriptionTask')}
          onCancel={closeAlertDeleteModal}
          onConfirm={onDeleteSelectedTasks}
        />
      </Box>
    </>
  );
};

export default ContactDetail;
